/**
 * ApprovalQueueScreen (QGS v2)
 *
 * Full-screen list of KO / Rework / FanNote items awaiting a Line
 * Leader signature. Rendered when a LL taps the "Approval Queue" tile
 * on the operator dashboard.  Rows are cards: form name, product,
 * operator, and datetime the request was raised, per spec.
 *
 * Reads from GET /api/frontend/qgs/reports/approvals/ (existing
 * endpoint from checkpoint 3's older sibling — R5.11). Approve /
 * Reject POST to /reports/approvals/{approval_id}/{approve|reject}/.
 * The LL identifies themselves through the session (they logged into
 * the PWA), so no PIN is required here — the PIN gate lives on the
 * KO-creation flow (KoApprovalModal).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Check, X, Clock, User, Package, FileText, AlertOctagon, Wrench } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import * as qgsApi from '../qgsApi';

const API = (process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000') + '/api/frontend/qgs';


function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium', timeStyle: 'short',
    });
  } catch { return iso; }
}


function TypeBadge({ type }) {
  if (type === 'KO') {
    return (
      <Badge className="bg-red-100 text-red-800 border border-red-200 gap-1">
        <AlertOctagon className="w-3 h-3" /> KO
      </Badge>
    );
  }
  if (type === 'REWORK') {
    return (
      <Badge className="bg-orange-100 text-orange-800 border border-orange-200 gap-1">
        <Wrench className="w-3 h-3" /> Rework
      </Badge>
    );
  }
  return (
    <Badge className="bg-slate-100 text-slate-800 border border-slate-200 gap-1">
      <FileText className="w-3 h-3" /> Note
    </Badge>
  );
}


export default function ApprovalQueueScreen({ onBack, user }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/reports/approvals/`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setRows(body.data || []);
    } catch (e) {
      setError(e.message || 'Failed to load approval queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, decision) => {
    if (!row.approval_id || busyId) return;
    let reason = '';
    if (decision === 'reject') {
      reason = window.prompt('Reason for rejection?') || '';
      if (!reason.trim()) return;
    }
    setBusyId(row.approval_id);
    try {
      const t = await (await import('../qgsApi')).__esModule ? null : null;
      // Use qgsApi.req via a thin fetch — we don't have a helper for arbitrary
      // paths, so post directly. Same origin-bypass CSRF pattern.
      const csrfRes = await fetch(`${API.replace('/api/frontend/qgs','')}/api/frontend/auth/csrf-token/`, { credentials: 'include' });
      let csrf = '';
      if (csrfRes.ok) {
        try { csrf = (await csrfRes.json())?.data?.csrfToken || ''; } catch {}
      }
      const res = await fetch(`${API}/reports/approvals/${row.approval_id}/${decision}/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRFToken': csrf } : {}) },
        body: JSON.stringify(decision === 'reject' ? { reason } : {}),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const b = await res.json(); if (b.error) msg = b.error; } catch {}
        alert(`${decision === 'approve' ? 'Approve' : 'Reject'} failed: ${msg}`);
        return;
      }
      await load();
    } catch (e) {
      alert(`${decision === 'approve' ? 'Approve' : 'Reject'} failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-4 flex items-center justify-between gap-3 sticky top-0 z-10">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">Approval Queue</h1>
          <p className="text-xs md:text-sm text-slate-500">
            KO / Rework / Note requests awaiting your signature{user?.username ? `, ${user.username}` : ''}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 md:p-6">
        {error && (
          <div className="p-3 rounded bg-red-50 border border-red-200 text-red-800 text-sm mb-4">
            {error}
          </div>
        )}

        {loading && rows.length === 0 && (
          <div className="p-8 text-center text-slate-500">Loading approvals…</div>
        )}

        {!loading && rows.length === 0 && (
          <div className="p-8 text-center text-slate-500 bg-white rounded-lg border border-dashed border-slate-300">
            Nothing waiting. All caught up.
          </div>
        )}

        <div className="space-y-4">
          {rows.map(row => (
            <Card key={row.approval_id} className="border-l-4 border-l-indigo-500 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                  <div className="min-w-0 flex-1 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <TypeBadge type={row.type} />
                      {row.ko_id && (
                        <span className="text-xs font-mono text-slate-500">{row.ko_id}</span>
                      )}
                      {row.gate && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">
                          {row.gate}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div className="flex items-center gap-2 text-slate-700 min-w-0">
                        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="truncate">
                          <span className="text-slate-500">Form:</span>{' '}
                          <span className="font-medium">{row.form_name || row.form || '—'}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-700 min-w-0">
                        <Package className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="truncate">
                          <span className="text-slate-500">Product:</span>{' '}
                          <span className="font-medium">{row.product_model || row.product || '—'}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-700 min-w-0">
                        <User className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="truncate">
                          <span className="text-slate-500">Operator:</span>{' '}
                          <span className="font-medium">{row.creator || '—'}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-700 min-w-0">
                        <Clock className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="truncate">
                          <span className="text-slate-500">Raised:</span>{' '}
                          <span className="font-medium">{fmtWhen(row.created_at)}</span>
                        </span>
                      </div>
                      {row.unit_serial && (
                        <div className="flex items-center gap-2 text-slate-700 min-w-0 md:col-span-2">
                          <span className="text-slate-500">Unit:</span>
                          <span className="font-mono text-xs">{row.unit_serial}</span>
                        </div>
                      )}
                      {(row.defect_type || row.defect_location) && (
                        <div className="flex items-center gap-2 text-slate-700 min-w-0 md:col-span-2">
                          <span className="text-slate-500">Defect:</span>
                          <span>{row.defect_type || '—'}{row.defect_location ? ` · ${row.defect_location}` : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0 w-full sm:w-32 md:self-center">
                    <Button
                      size="sm"
                      className="w-full justify-center bg-green-600 hover:bg-green-700 text-white shadow-sm"
                      onClick={() => decide(row, 'approve')}
                      disabled={busyId === row.approval_id}
                    >
                      <Check className="w-4 h-4 mr-1.5" />
                      {busyId === row.approval_id ? 'Working…' : 'Approve'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full justify-center border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => decide(row, 'reject')}
                      disabled={busyId === row.approval_id}
                    >
                      <X className="w-4 h-4 mr-1.5" />
                      Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
